import React, { useEffect, useState } from 'react';
import styles from './AppointmentInfo.module.css';
import accents from '../moduleCard/accents.module.css';
import CalendarConfig from '../../../../consts/CalendarConfig';
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CategoryIcon from '@mui/icons-material/Category';
import TimerIcon from '@mui/icons-material/Timer';
import EditIcon from '@mui/icons-material/Edit';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { Tooltip } from "@mui/material";
import DemoTag from '../../../../components/demo-data/DemoTag';
import { isDemoRecord } from '../../../../components/demo-data/demoData';
import { procedureLabels } from '../../../appointments/v2/utils/appointmentUtils';
import { isOnlineAppointment } from '../../../appointments/v2/utils/attendanceMode';
import { isInsuranceAppointment, coverageLabel } from '../../../appointments/v2/utils/billingType';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';

// Selo de situação do atendimento, por status da página (mesmas chaves de Appointment.js)
const STATUS_PILL = {
  notInitialized: { label: 'Preparação', className: 'statusPending' },
  initialized: { label: 'Em andamento', className: 'statusActive' },
  // Cronômetro parado, nada gravado ainda: só vira "Registrado" depois de confirmar o resumo
  finished: { label: 'Falta registrar', className: 'statusPending' },
  registered: { label: 'Registrado', className: 'statusRegistered' },
};

/** Bloco pequeno de informação do agendamento: ícone colorido, rótulo e valor. */
const InfoTile = ({ icon, label, value, accent, children }) => (
  <div className={`${styles.tile} ${accents[accent] || accents.blue}`}>
    <div className={styles.tileIcon}>{icon}</div>
    <div className={styles.tileContent}>
      <span className={styles.tileLabel}>{label}</span>
      <span className={styles.tileValue}>{value}</span>
    </div>
    {children}
  </div>
);

/**
 * Card do atendimento: procedimento com o selo de situação à esquerda e as
 * ações (cronômetro + botão da etapa) à direita; abaixo, os blocos de data,
 * horário, valor, sala e tipo.
 *
 * `appointmentTypeLabel` é o tipo de atendimento em vigor; `onChangeTypeClick` nulo
 * esconde o lápis (antes de iniciar, depois de registrar ou com um único tipo).
 * `reportButton`: botão "Exportar relatório completo", exibido antes do botão da etapa.
 */
const AppointmentInfo = ({ data, enabled, elapsedTime, button, reportButton, appointmentTypeLabel, onChangeTypeClick, status }) => {

  const { CompanyConfig } = DataProviderInstance();
  const [roomName, setRoomName] = useState(null);
  // Todas as linhas de procedimento ("A + B + C"); sem linha gravada cai no principal
  const procedureTitle = procedureLabels(data, CompanyConfig?.Procedures) || "Procedimento não informado";

  // Buscar informações da sala quando roomId estiver disponível
  useEffect(() => {
    if (data?.roomId && CompanyConfig?.Rooms) {
      const room = CompanyConfig.Rooms.find(r => r.Id === data.roomId);
      setRoomName(room ? room.Label : null);
    } else {
      setRoomName(null);
    }
  }, [data?.roomId, CompanyConfig?.Rooms]);

  function formatElapsedTime(currentSeconds) {
    let hours = Math.floor(currentSeconds / 3600);
    let minutes = Math.floor((currentSeconds % 3600) / 60);
    let seconds = currentSeconds % 60;

    let formattedHours = hours.toString().padStart(2, '0');
    let formattedMinutes = minutes.toString().padStart(2, '0');
    let formattedSeconds = seconds.toString().padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  }

  const secondValue = data?.date ? data.date.split(" ")[1]?.split(":").slice(0, 2).join(":") : "00:00";
  const pill = STATUS_PILL[status];
  // Cobertura só para quem trabalha com convênio (ou atendimento já por convênio)
  const showCoverage = isInsuranceAppointment(data) || (CompanyConfig?.InsuranceProviders || []).length > 0;

  return (
    <div className={styles.hero}>
      <div className={styles.topRow}>
        <div className={styles.procedureTitle}>
          <div className={styles.eyebrowRow}>
            <span className={styles.eyebrow}>Atendimento</span>
            {pill && (
              <span className={`${styles.statusPill} ${styles[pill.className]}`}>
                <span className={styles.statusDot} />
                {pill.label}
              </span>
            )}
            {isDemoRecord(data) && <DemoTag />}
          </div>
          <h2 className={styles.procedureName} title={procedureTitle}>
            {procedureTitle}
          </h2>
        </div>

        <div className={styles.actions}>
          {enabled && (
            <Tooltip title="Tempo decorrido do atendimento">
              <div className={styles.timer}>
                <TimerIcon />
                <div className={styles.timerTexts}>
                  <span className={styles.timerLabel}>Tempo decorrido</span>
                  <span className={styles.timerValue}>{formatElapsedTime(elapsedTime)}</span>
                </div>
              </div>
            </Tooltip>
          )}

          {reportButton && (
            <div className={styles.buttonContainer}>
              {reportButton}
            </div>
          )}

          <div className={styles.buttonContainer}>
            {button}
          </div>
        </div>
      </div>

        <div className={styles.tiles}>
          <InfoTile
            icon={<CalendarTodayIcon />}
            label="Data"
            accent="blue"
            value={data?.date ? CalendarConfig.GetFullDateName(CalendarConfig.GetDDMMAAAA(data.date)) : "Não informada"}
          />

          <InfoTile icon={<AccessTimeIcon />} label="Horário" accent="purple" value={secondValue} />

          {data?.price && (
            <InfoTile
              icon={<MonetizationOnIcon />}
              label="Valor"
              accent="green"
              value={`R$ ${parseFloat(data.price).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            />
          )}

          {isOnlineAppointment(data) ? (
            <InfoTile icon={<VideocamOutlinedIcon />} label="Modalidade" accent="teal" value="Online">
              {data?.meeting_url && (
                <button
                  className={styles.changeModelButton}
                  onClick={() => window.open(data.meeting_url, '_blank', 'noopener,noreferrer')}
                  title="Abrir videochamada"
                  aria-label="Abrir videochamada"
                >
                  <OpenInNewIcon fontSize="small" />
                </button>
              )}
            </InfoTile>
          ) : data?.roomId && (
            <InfoTile icon={<MeetingRoomIcon />} label="Sala" accent="amber" value={roomName || 'Sala inativa'} />
          )}

          {showCoverage && (
            <InfoTile icon={<BadgeOutlinedIcon />} label="Cobertura" accent="blue" value={coverageLabel(data)} />
          )}

          {appointmentTypeLabel && (
            <InfoTile icon={<CategoryIcon />} label="Tipo" accent="pink" value={appointmentTypeLabel}>
              {onChangeTypeClick && (
                <button
                  className={styles.changeModelButton}
                  onClick={onChangeTypeClick}
                  title="Alterar tipo de atendimento"
                  aria-label="Alterar tipo de atendimento"
                >
                  <EditIcon fontSize="small" />
                </button>
              )}
            </InfoTile>
          )}
        </div>
    </div>
  );
};

export default AppointmentInfo;
